/**
 * Shared formatting for the metrics page. German locale, since the app's UI is German.
 *
 * The rate helpers deliberately distinguish null ("no denominator") from 0 ("a real
 * zero"). The old page collapsed both to "0,0%", which made a broken RPC look like a
 * rep who simply sold nothing.
 */

import type { Rate } from "../../services/sales-metrics";

const LOCALE = "de-DE";

export function formatCurrency(value: number, compact = false): string {
  return new Intl.NumberFormat(LOCALE, {
    style: "currency",
    currency: "EUR",
    notation: compact ? "compact" : "standard",
    maximumFractionDigits: compact ? 1 : 0,
  }).format(value);
}

export function formatNumber(value: number, compact = false): string {
  return new Intl.NumberFormat(LOCALE, {
    notation: compact ? "compact" : "standard",
    maximumFractionDigits: compact ? 1 : 0,
  }).format(value);
}

/** Renders a rate as a percentage, or an em dash when there is no denominator. */
export function formatRate(rate: Rate, fractionDigits = 1): string {
  if (rate === null) return "—";
  return new Intl.NumberFormat(LOCALE, {
    style: "percent",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(rate);
}

/**
 * A rate plus the denominator it was computed from.
 *
 * Always used for funnel rates: the schema cannot observe doors that were approached
 * but never recorded, so a bare percentage would imply coverage the data doesn't have.
 */
export function formatRateWithBase(
  rate: Rate,
  base: number,
  baseLabel = "erfasste Türen"
): string {
  if (rate === null) return `— (0 ${baseLabel})`;
  return `${formatRate(rate)} von ${formatNumber(base)} ${baseLabel}`;
}

export function formatDelta(current: number, previous: number): {
  text: string;
  direction: "up" | "down" | "flat";
} {
  if (previous === 0) {
    return { text: current === 0 ? "±0%" : "neu", direction: current === 0 ? "flat" : "up" };
  }
  const change = (current - previous) / Math.abs(previous);
  const direction = change > 0.0005 ? "up" : change < -0.0005 ? "down" : "flat";
  const sign = direction === "up" ? "+" : "";
  return {
    text: `${sign}${new Intl.NumberFormat(LOCALE, {
      style: "percent",
      maximumFractionDigits: 1,
    }).format(change)}`,
    direction,
  };
}

/** Formats a YYYY-MM-DD bucket for an axis tick. */
export function formatBucket(bucket: string, grain: "day" | "week" | "month"): string {
  const date = new Date(`${bucket}T00:00:00`);
  if (grain === "month") {
    return new Intl.DateTimeFormat(LOCALE, { month: "short", year: "2-digit" }).format(date);
  }
  return new Intl.DateTimeFormat(LOCALE, { day: "2-digit", month: "2-digit" }).format(date);
}

/** Median of a numeric list, ignoring nulls. Used for benchmark comparisons. */
export function median(values: (number | null)[]): number | null {
  const clean = values.filter((v): v is number => v !== null && Number.isFinite(v));
  if (clean.length === 0) return null;
  const sorted = [...clean].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1]! + sorted[mid]!) / 2
    : sorted[mid]!;
}
